/**
 * 买分：申请（玩家）与审批（房主）。
 *
 * ── 三条不能动的交互约束 ─────────────────────────────────────
 *
 * 1. **平时不占位置**。筹码还厚的时候这颗按钮什么都不解决，却要一直和底牌、底池
 *    抢那块 390×844 的屏幕。所以入口只在筹码低于 `TOPUP_LOW_STACK_BB` 个大盲时出现；
 *    **已达起始线时是不出现，不是置灰** —— 一颗永远灰着的按钮只会被反复去点。
 *
 * 2. **审批绝不用弹窗**。房主可能正在自己的回合上，一个模态盖住牌桌会让他直接超时。
 *    提示是顶栏里的一个角标，点开才是列表。
 *
 * 3. **批准 ≠ 到账**（红线 #41）。手牌进行中批准的买分要排队到手牌边界，
 *    这段等待期在两侧都必须**说出来**：申请人看到「本手后到账」，
 *    房主那一行也变成「本手后到账」。不说的话双方都以为没生效，然后再点一次。
 *
 * ★ 红线 #38：上行的 `requestTopUp` **没有金额字段**，界面上的「最多补 1,200」
 *   是服务端算好的预估值，纯展示。
 */
import { useCallback, useEffect } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { PlayerView, TopUpRequestView } from '@poker/shared'

import { useModalDialog } from '../hooks/useModalDialog.js'
import { useOpenTapGuard } from '../hooks/useOpenTapGuard.js'
import { IconCheck, IconChips, IconClose } from '../icons/index.js'
import { useUiStore } from '../store/index.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'

import { topUpErrorText } from './errorText.js'
import {
  formatExact,
  pendingTopUpCount,
  pendingTopUps,
  approvedTopUps,
  settlementOf,
  showTopUpQueue,
  stackOfSeat,
  topUpEntry,
  topUpIsBot,
  waitedText,
  waitingRoomOwnsTopUp,
  type TopUpEntryModel,
} from './topUpDerive.js'
import { handEndOverlayOpen } from './readyDerive.js'
import { useRoomLink } from './roomLink.js'
import { topUpChipSpot } from './topUpSpot.js'
import { useTableLayout } from '../table/TableScene.js'
import { useTickingNow } from './useNow.js'
import './room.css'

/** 「等了多久」的刷新间隔。分钟级文案，30s 一跳足够，也不会变成每帧重渲染。 */
const WAIT_TICK_MS = 30_000

// ============================================================================
// 玩家侧：入口
// ============================================================================

const ENTRY_LABEL: Readonly<Record<'offer' | 'pending' | 'approved', string>> = {
  offer: '买分',
  pending: '等房主批准',
  approved: '本手后到账',
}

export interface TopUpEntryProps {
  /** 覆盖 store 里的视图（回放 / storybook / 单测用）；省略就订阅 store。 */
  readonly view?: PlayerView | null
  /**
   * 等待室开着。抽屉替胶囊说了话的时候（自己暂离且筹码用完：主按钮位置是「买分」或
   * 「等房主批准 / 本手后到账」，见 waitingRoomOwnsTopUp）不画胶囊 —— 否则同一个动作一屏两个入口。
   * 其余时候抽屉里没有买分入口，胶囊照画。买分结果的 Toast 照常。
   */
  readonly waitingOpen?: boolean
}

/**
 * 筹码见底时出现在舞台一角的小胶囊。点开才是面板。
 *
 * 为什么不直接把「申请买分」做成一颗按钮：申请之前玩家要先知道自己现在多少、
 * 能补多少 —— 那两个数字放不进胶囊，而放进去就成了常驻横幅。
 */
export function TopUpEntry({ view: viewProp, waitingOpen = false }: TopUpEntryProps = {}): ReactNode {
  const storeView = useNetStore(selectNetView)
  const view = viewProp === undefined ? storeView : viewProp
  const openSheet = useUiStore((s) => s.openSheet)
  const layout = useTableLayout()
  const entry = topUpEntry(view)

  useTopUpResultToast(view)

  if (entry.kind === 'hidden' || (waitingOpen && waitingRoomOwnsTopUp(view))) return null

  // ★ 结算浮层满宽贴底，会把这颗胶囊整块盖住 —— 而那正是最需要买分的时刻
  //   （刚输光，下一手 10 秒后自动开始）。开着的时候只在上面两个角里挑，互不遮挡。
  const settling = handEndOverlayOpen(view, settlementOf(view) !== null)
  // ★ 放哪个角由牌桌几何决定：手机和折叠屏上自己的座位就在左下角，
  //   固定左下会压住自己的名字和筹码（见 topUpSpot.ts）。几何还没量出来时退回 CSS 的左下角
  const spot = layout === null ? null : topUpChipSpot(layout, settling)
  const style: CSSProperties | undefined =
    spot === null
      ? undefined
      : {
          left: spot.left ?? 'auto',
          right: spot.right ?? 'auto',
          top: spot.top ?? 'auto',
          bottom: spot.bottom ?? 'auto',
        }

  return (
    <div
      className="topup-entry"
      data-settling={settling ? 'true' : undefined}
      data-corner={spot?.corner}
      style={style}
    >
      <button
        type="button"
        className={`btn btn--quiet topup-chip topup-chip--${entry.kind}`}
        onClick={() => openSheet('topUp')}
      >
        <IconChips size={16} />
        {ENTRY_LABEL[entry.kind]}
      </button>
    </div>
  )
}

/**
 * 自己那笔申请有结果时冒一条 Toast。
 *
 * 只订自己座位的那条：别人的买分被拒与我无关，弹出来只是噪音。
 * 「已批准待生效」不弹 —— 胶囊已经改写成「本手后到账」，同一件事说两遍。
 */
function useTopUpResultToast(view: PlayerView | null): void {
  const link = useRoomLink()
  const showToast = useUiStore((s) => s.showToast)
  const seat = view?.viewer.ownedSeat ?? null

  useEffect(() => {
    const sub = link.onMessage
    if (sub === undefined || seat === null) return undefined
    return sub((m) => {
      if (m.type !== 'topUpResolved' || m.seat !== seat) return
      if (m.outcome === 'approved') {
        // 带 appliedAt 的那条才是真到账；无 appliedAt 的「已批准」由胶囊承载
        if (m.appliedAt !== undefined) showToast(`买分到账 +${formatExact(m.amount ?? 0)}`, 'success')
        return
      }
      if (m.outcome === 'denied') showToast('房主没批准这次买分', 'warn')
      else if (m.outcome === 'expired') showToast('买分申请超时，已作废', 'warn')
      // cancelled 是自己撤的（或离座了），不用再说一遍
    })
  }, [link, seat, showToast])
}

// ============================================================================
// 玩家侧：申请面板
// ============================================================================

/** 服务端拒绝 → Toast。买分的拒绝原因由服务端给（带具体额度），本地不改写。 */
function useTopUpErrorToast(): void {
  const lastError = useNetStore((s) => s.lastError)
  const clearError = useNetStore((s) => s.clearError)
  const showToast = useUiStore((s) => s.showToast)

  useEffect(() => {
    if (lastError === null) return
    if (lastError.code !== 'E_TOPUP_NOT_ALLOWED' && lastError.code !== 'E_TOPUP_NOT_FOUND') return
    showToast(topUpErrorText(lastError.code, lastError.message), 'warn')
    clearError()
  }, [lastError, clearError, showToast])
}

export function TopUpSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const view = useNetStore(selectNetView)
  const link = useRoomLink()
  const entry = topUpEntry(view)
  const open = sheet === 'topUp' && entry.kind !== 'hidden'
  const { dialogRef, onDialogKeyDown } = useModalDialog({ open, onClose: closeSheet })
  // 从「我的座位」的「买分」行或舞台胶囊上打开：双击的第二下不许直接落到「申请买分」上
  const guardTap = useOpenTapGuard(open)

  useTopUpErrorToast()

  const request = useCallback((): void => {
    // ★ 红线 #38：只发意图，不发金额
    link.send({ type: 'requestTopUp' })
    closeSheet()
  }, [link, closeSheet])

  const cancel = useCallback((): void => {
    const id = topUpEntry(useNetStore.getState().view).requestId
    if (id === null) return
    link.send({ type: 'cancelTopUp', requestId: id })
    closeSheet()
  }, [link, closeSheet])

  if (!open) return null

  return (
    <div className="sheet-scrim sheet-scrim--paper" onClickCapture={guardTap} onClick={closeSheet} role="presentation">
      <div
        className="sheet"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="买分"
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <strong>买分</strong>
          <span className="sheet__sub num">起始线 {formatExact(entry.startingStack)}</span>
        </header>

        <TopUpFacts entry={entry} />

        {entry.kind === 'offer' ? (
          <button type="button" className="btn btn--cta pressable" onClick={request}>
            申请买分
          </button>
        ) : (
          <button type="button" className="btn btn--quiet" onClick={cancel}>
            撤回申请
          </button>
        )}

        <button type="button" className="btn btn--ghost sheet__close" onClick={closeSheet}>
          关闭
        </button>
      </div>
    </div>
  )
}

function TopUpFacts({ entry }: { readonly entry: TopUpEntryModel }): ReactNode {
  return (
    <dl className="topup-facts">
      <div>
        <dt>现在</dt>
        <dd className="num">{formatExact(entry.stack)}</dd>
      </div>
      <div>
        <dt>最多补</dt>
        <dd className="num">{formatExact(entry.allowance)}</dd>
      </div>
      {entry.kind !== 'offer' && (
        <div>
          <dt>状态</dt>
          {/* ★ 「已批准」与「已到账」是两件事：手牌进行中批准的要等手牌边界（红线 #41）。
              不把这段等待期说出来，玩家会以为批准没生效而反复再申请。 */}
          <dd className="topup-facts__state">
            {entry.kind === 'approved' ? '已批准，本手后到账' : '等房主批准'}
          </dd>
        </div>
      )}
    </dl>
  )
}

// ============================================================================
// 房主侧：角标 + 队列
// ============================================================================

export interface TopUpQueueBadgeProps {
  readonly view?: PlayerView | null
}

/**
 * 顶栏里的审批角标。**不是弹窗** —— 房主可能正在自己的回合上，
 * 一个模态盖住牌桌等于直接让他超时（然后自动弃牌）。
 *
 * 待审批时显示笔数；全部批完、只剩等生效的那几笔时收成一个勾，
 * 让房主知道「已经处理过了」而不是继续被一个数字催。
 */
export function TopUpQueueBadge({ view: viewProp }: TopUpQueueBadgeProps = {}): ReactNode {
  const storeView = useNetStore(selectNetView)
  const view = viewProp === undefined ? storeView : viewProp
  const openSheet = useUiStore((s) => s.openSheet)

  if (!showTopUpQueue(view)) return null
  const pending = pendingTopUpCount(view)

  return (
    <button
      type="button"
      className={pending > 0 ? 'btn btn--bar topup-badge is-pending' : 'btn btn--bar topup-badge'}
      aria-label={pending > 0 ? `${pending} 笔买分待批准` : '买分已批准，本手后到账'}
      onClick={() => openSheet('topUpQueue')}
    >
      <IconChips size={16} />
      {pending > 0 ? <span className="num">{pending}</span> : <IconCheck size={14} />}
    </button>
  )
}

export function TopUpQueueSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const view = useNetStore(selectNetView)
  const link = useRoomLink()
  const now = useTickingNow(sheet === 'topUpQueue', WAIT_TICK_MS)
  const open = sheet === 'topUpQueue' && view !== null
  const { dialogRef, onDialogKeyDown } = useModalDialog({ open, onClose: closeSheet })
  // 等待室「处理买分」就在抽屉底部：双击它，第二下不许替房主点了批准 / 拒绝
  const guardTap = useOpenTapGuard(open)

  const approve = useCallback(
    (requestId: string): void => {
      link.send({ type: 'approveTopUp', requestId })
    },
    [link],
  )
  const deny = useCallback(
    (requestId: string): void => {
      link.send({ type: 'denyTopUp', requestId })
    },
    [link],
  )

  if (!open) return null

  return (
    <div className="sheet-scrim sheet-scrim--paper" onClickCapture={guardTap} onClick={closeSheet} role="presentation">
      <div
        className="sheet"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="买分申请"
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <strong>买分申请</strong>
          <span className="sheet__sub num">起始线 {formatExact(view.room.config.startingStack)}</span>
        </header>

        <TopUpQueueList view={view} now={now} onApprove={approve} onDeny={deny} />

        <button type="button" className="btn btn--ghost sheet__close" onClick={closeSheet}>
          关闭
        </button>
      </div>
    </div>
  )
}

export interface TopUpQueueListProps {
  readonly view: PlayerView
  readonly now: number
  readonly onApprove?: (requestId: string) => void
  readonly onDeny?: (requestId: string) => void
}

/**
 * 审批列表本体。拆出来是为了能直接喂 props 静态渲染（node 测试里 zustand 只读初值，
 * 面板开合挂在 store 上的那一层渲染不出来）。
 */
export function TopUpQueueList({ view, now, onApprove, onDeny }: TopUpQueueListProps): ReactNode {
  const pending = pendingTopUps(view)
  const approved = approvedTopUps(view)
  if (pending.length === 0 && approved.length === 0) return <p className="sheet__empty">没有待处理的申请</p>
  return (
    <ul className="topup-list" role="list">
      {pending.map((r) => (
        <TopUpRow
          key={r.requestId}
          req={r}
          stack={stackOfSeat(view, r.seat)}
          isBot={topUpIsBot(view, r)}
          now={now}
          {...(onApprove === undefined ? {} : { onApprove })}
          {...(onDeny === undefined ? {} : { onDeny })}
        />
      ))}
      {approved.map((r) => (
        <TopUpRow key={r.requestId} req={r} stack={stackOfSeat(view, r.seat)} isBot={topUpIsBot(view, r)} now={now} />
      ))}
    </ul>
  )
}

interface TopUpRowProps {
  readonly req: TopUpRequestView
  readonly stack: number
  /** AI 输光后会自己交申请（服务端 room/botBuyIn.ts）：房主得一眼看出这笔是 AI 的 */
  readonly isBot: boolean
  readonly now: number
  readonly onApprove?: (requestId: string) => void
  readonly onDeny?: (requestId: string) => void
}

function TopUpRow({ req, stack, isBot, now, onApprove, onDeny }: TopUpRowProps): ReactNode {
  const approvedRow = req.status === 'approved'
  return (
    <li className={approvedRow ? 'topup-row topup-row--approved' : 'topup-row'}>
      <span className="topup-row__who">
        <span className="topup-row__line">
          <span className="topup-row__name">{req.nickname === '' ? `${req.seat + 1} 号位` : req.nickname}</span>
          {isBot && <span className="topup-row__tag">AI</span>}
        </span>
        <span className="topup-row__meta">
          {/* 房主的判断依据就这三个数：他现在多少、要补多少、等了多久 */}
          <span className="num">{formatExact(stack)}</span>
          <span aria-hidden="true"> · </span>
          <span className="num">+{formatExact(req.amount)}</span>
          <span aria-hidden="true"> · </span>
          {approvedRow ? '本手后到账' : waitedText(req.requestedAt, now)}
        </span>
      </span>

      {approvedRow ? (
        <span className="badge badge--host">已批准</span>
      ) : (
        <span className="topup-row__acts">
          <button
            type="button"
            className="btn btn--kick hit-expand"
            aria-label={`拒绝 ${req.nickname}`}
            onClick={() => onDeny?.(req.requestId)}
          >
            <IconClose size={18} />
          </button>
          <button
            type="button"
            className="btn btn--primary topup-ok"
            aria-label={`批准 ${req.nickname}`}
            onClick={() => onApprove?.(req.requestId)}
          >
            <IconCheck size={18} />
          </button>
        </span>
      )}
    </li>
  )
}

export default TopUpEntry
